/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  GrantNexusProjectPermissionsPathParameters,
  LeaveNexusProjectPathParameters,
  ListNexusProjectPermissionsParams,
  ListNexusProjectPermissionsPathParameters,
  RevokeNexusProjectPermissionsPathParameters,
  TLHTTPValidationError,
  TLNexusGrantPermissionsRequest,
  TLNexusLeaveResponse,
  TLNexusPermissionListResponse,
  TLNexusProjectPermission,
  TLNexusRevokePermissionsRequest,
  TLNexusRevokePermissionsResponse,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary List Nexus Project Permissions
 */
export const listNexusProjectPermissions = (
  { projectId }: ListNexusProjectPermissionsPathParameters,
  params?: ListNexusProjectPermissionsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusPermissionListResponse>(
    { url: `/nexus/projects/${projectId}/permissions`, method: 'GET', params, signal },
    options,
  );
};

export const getListNexusProjectPermissionsQueryKey = (
  { projectId }: ListNexusProjectPermissionsPathParameters,
  params?: ListNexusProjectPermissionsParams,
) => {
  return ['nexus', 'projects', projectId, 'permissions', ...(params ? [params] : [])] as const;
};

export const getListNexusProjectPermissionsQueryOptions = <
  TData = Awaited<ReturnType<typeof listNexusProjectPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: ListNexusProjectPermissionsPathParameters,
  params?: ListNexusProjectPermissionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listNexusProjectPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListNexusProjectPermissionsQueryKey({ projectId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listNexusProjectPermissions>>> = ({ signal }) =>
    listNexusProjectPermissions({ projectId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listNexusProjectPermissions>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListNexusProjectPermissionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listNexusProjectPermissions>>
>;
export type ListNexusProjectPermissionsQueryError = ErrorType<TLHTTPValidationError>;

export function useListNexusProjectPermissions<
  TData = Awaited<ReturnType<typeof listNexusProjectPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListNexusProjectPermissionsPathParameters,
  params: undefined | ListNexusProjectPermissionsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listNexusProjectPermissions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNexusProjectPermissions>>,
          TError,
          Awaited<ReturnType<typeof listNexusProjectPermissions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListNexusProjectPermissions<
  TData = Awaited<ReturnType<typeof listNexusProjectPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListNexusProjectPermissionsPathParameters,
  params?: ListNexusProjectPermissionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listNexusProjectPermissions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNexusProjectPermissions>>,
          TError,
          Awaited<ReturnType<typeof listNexusProjectPermissions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListNexusProjectPermissions<
  TData = Awaited<ReturnType<typeof listNexusProjectPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListNexusProjectPermissionsPathParameters,
  params?: ListNexusProjectPermissionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listNexusProjectPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Nexus Project Permissions
 */

export function useListNexusProjectPermissions<
  TData = Awaited<ReturnType<typeof listNexusProjectPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: ListNexusProjectPermissionsPathParameters,
  params?: ListNexusProjectPermissionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listNexusProjectPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListNexusProjectPermissionsQueryOptions({ projectId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Nexus Project Permissions
 */
export const prefetchListNexusProjectPermissionsQuery = async <
  TData = Awaited<ReturnType<typeof listNexusProjectPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: ListNexusProjectPermissionsPathParameters,
  params?: ListNexusProjectPermissionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listNexusProjectPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListNexusProjectPermissionsQueryOptions({ projectId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Nexus Project Permissions
 */
export const invalidateListNexusProjectPermissions = async (
  queryClient: QueryClient,
  { projectId }: ListNexusProjectPermissionsPathParameters,
  params?: ListNexusProjectPermissionsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getListNexusProjectPermissionsQueryKey({ projectId }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Grant Nexus Project Permissions
 */
export const grantNexusProjectPermissions = (
  { projectId }: GrantNexusProjectPermissionsPathParameters,
  tLNexusGrantPermissionsRequest: BodyType<TLNexusGrantPermissionsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusProjectPermission[]>(
    {
      url: `/nexus/projects/${projectId}/permissions`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusGrantPermissionsRequest,
      signal,
    },
    options,
  );
};

export const getGrantNexusProjectPermissionsQueryKey = (
  { projectId }: GrantNexusProjectPermissionsPathParameters,
  tLNexusGrantPermissionsRequest?: BodyType<TLNexusGrantPermissionsRequest>,
) => {
  return ['POST', 'nexus', 'projects', projectId, 'permissions', tLNexusGrantPermissionsRequest] as const;
};

export const getGrantNexusProjectPermissionsQueryOptions = <
  TData = Awaited<ReturnType<typeof grantNexusProjectPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: GrantNexusProjectPermissionsPathParameters,
  tLNexusGrantPermissionsRequest: BodyType<TLNexusGrantPermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof grantNexusProjectPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGrantNexusProjectPermissionsQueryKey({ projectId }, tLNexusGrantPermissionsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof grantNexusProjectPermissions>>> = ({ signal }) =>
    grantNexusProjectPermissions({ projectId }, tLNexusGrantPermissionsRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof grantNexusProjectPermissions>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GrantNexusProjectPermissionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof grantNexusProjectPermissions>>
>;
export type GrantNexusProjectPermissionsQueryError = ErrorType<TLHTTPValidationError>;

export function useGrantNexusProjectPermissions<
  TData = Awaited<ReturnType<typeof grantNexusProjectPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GrantNexusProjectPermissionsPathParameters,
  tLNexusGrantPermissionsRequest: BodyType<TLNexusGrantPermissionsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof grantNexusProjectPermissions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof grantNexusProjectPermissions>>,
          TError,
          Awaited<ReturnType<typeof grantNexusProjectPermissions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGrantNexusProjectPermissions<
  TData = Awaited<ReturnType<typeof grantNexusProjectPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GrantNexusProjectPermissionsPathParameters,
  tLNexusGrantPermissionsRequest: BodyType<TLNexusGrantPermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof grantNexusProjectPermissions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof grantNexusProjectPermissions>>,
          TError,
          Awaited<ReturnType<typeof grantNexusProjectPermissions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGrantNexusProjectPermissions<
  TData = Awaited<ReturnType<typeof grantNexusProjectPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GrantNexusProjectPermissionsPathParameters,
  tLNexusGrantPermissionsRequest: BodyType<TLNexusGrantPermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof grantNexusProjectPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Grant Nexus Project Permissions
 */

export function useGrantNexusProjectPermissions<
  TData = Awaited<ReturnType<typeof grantNexusProjectPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: GrantNexusProjectPermissionsPathParameters,
  tLNexusGrantPermissionsRequest: BodyType<TLNexusGrantPermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof grantNexusProjectPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGrantNexusProjectPermissionsQueryOptions(
    { projectId },
    tLNexusGrantPermissionsRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Grant Nexus Project Permissions
 */
export const prefetchGrantNexusProjectPermissionsQuery = async <
  TData = Awaited<ReturnType<typeof grantNexusProjectPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: GrantNexusProjectPermissionsPathParameters,
  tLNexusGrantPermissionsRequest: BodyType<TLNexusGrantPermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof grantNexusProjectPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGrantNexusProjectPermissionsQueryOptions(
    { projectId },
    tLNexusGrantPermissionsRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Grant Nexus Project Permissions
 */
export const invalidateGrantNexusProjectPermissions = async (
  queryClient: QueryClient,
  { projectId }: GrantNexusProjectPermissionsPathParameters,
  tLNexusGrantPermissionsRequest: BodyType<TLNexusGrantPermissionsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGrantNexusProjectPermissionsQueryKey({ projectId }, tLNexusGrantPermissionsRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Leave Nexus Project
 */
export const leaveNexusProject = (
  { projectId }: LeaveNexusProjectPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusLeaveResponse>(
    { url: `/nexus/projects/${projectId}/permissions/me`, method: 'DELETE', signal },
    options,
  );
};

export const getLeaveNexusProjectQueryKey = ({ projectId }: LeaveNexusProjectPathParameters) => {
  return ['DELETE', 'nexus', 'projects', projectId, 'permissions', 'me'] as const;
};

export const getLeaveNexusProjectQueryOptions = <
  TData = Awaited<ReturnType<typeof leaveNexusProject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: LeaveNexusProjectPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof leaveNexusProject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getLeaveNexusProjectQueryKey({ projectId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof leaveNexusProject>>> = ({ signal }) =>
    leaveNexusProject({ projectId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof leaveNexusProject>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type LeaveNexusProjectQueryResult = NonNullable<Awaited<ReturnType<typeof leaveNexusProject>>>;
export type LeaveNexusProjectQueryError = ErrorType<TLHTTPValidationError>;

export function useLeaveNexusProject<
  TData = Awaited<ReturnType<typeof leaveNexusProject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: LeaveNexusProjectPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof leaveNexusProject>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof leaveNexusProject>>,
          TError,
          Awaited<ReturnType<typeof leaveNexusProject>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useLeaveNexusProject<
  TData = Awaited<ReturnType<typeof leaveNexusProject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: LeaveNexusProjectPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof leaveNexusProject>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof leaveNexusProject>>,
          TError,
          Awaited<ReturnType<typeof leaveNexusProject>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useLeaveNexusProject<
  TData = Awaited<ReturnType<typeof leaveNexusProject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: LeaveNexusProjectPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof leaveNexusProject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Leave Nexus Project
 */

export function useLeaveNexusProject<
  TData = Awaited<ReturnType<typeof leaveNexusProject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: LeaveNexusProjectPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof leaveNexusProject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getLeaveNexusProjectQueryOptions({ projectId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Leave Nexus Project
 */
export const prefetchLeaveNexusProjectQuery = async <
  TData = Awaited<ReturnType<typeof leaveNexusProject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: LeaveNexusProjectPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof leaveNexusProject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getLeaveNexusProjectQueryOptions({ projectId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Leave Nexus Project
 */
export const invalidateLeaveNexusProject = async (
  queryClient: QueryClient,
  { projectId }: LeaveNexusProjectPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getLeaveNexusProjectQueryKey({ projectId }) }, options);

  return queryClient;
};

/**
 * @summary Revoke Nexus Project Permissions
 */
export const revokeNexusProjectPermissions = (
  { projectId }: RevokeNexusProjectPermissionsPathParameters,
  tLNexusRevokePermissionsRequest: BodyType<TLNexusRevokePermissionsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusRevokePermissionsResponse>(
    {
      url: `/nexus/projects/${projectId}/permissions/revoke`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusRevokePermissionsRequest,
      signal,
    },
    options,
  );
};

export const getRevokeNexusProjectPermissionsQueryKey = (
  { projectId }: RevokeNexusProjectPermissionsPathParameters,
  tLNexusRevokePermissionsRequest?: BodyType<TLNexusRevokePermissionsRequest>,
) => {
  return ['POST', 'nexus', 'projects', projectId, 'permissions', 'revoke', tLNexusRevokePermissionsRequest] as const;
};

export const getRevokeNexusProjectPermissionsQueryOptions = <
  TData = Awaited<ReturnType<typeof revokeNexusProjectPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: RevokeNexusProjectPermissionsPathParameters,
  tLNexusRevokePermissionsRequest: BodyType<TLNexusRevokePermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeNexusProjectPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getRevokeNexusProjectPermissionsQueryKey({ projectId }, tLNexusRevokePermissionsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof revokeNexusProjectPermissions>>> = ({ signal }) =>
    revokeNexusProjectPermissions({ projectId }, tLNexusRevokePermissionsRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof revokeNexusProjectPermissions>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RevokeNexusProjectPermissionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof revokeNexusProjectPermissions>>
>;
export type RevokeNexusProjectPermissionsQueryError = ErrorType<TLHTTPValidationError>;

export function useRevokeNexusProjectPermissions<
  TData = Awaited<ReturnType<typeof revokeNexusProjectPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RevokeNexusProjectPermissionsPathParameters,
  tLNexusRevokePermissionsRequest: BodyType<TLNexusRevokePermissionsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeNexusProjectPermissions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof revokeNexusProjectPermissions>>,
          TError,
          Awaited<ReturnType<typeof revokeNexusProjectPermissions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRevokeNexusProjectPermissions<
  TData = Awaited<ReturnType<typeof revokeNexusProjectPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RevokeNexusProjectPermissionsPathParameters,
  tLNexusRevokePermissionsRequest: BodyType<TLNexusRevokePermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeNexusProjectPermissions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof revokeNexusProjectPermissions>>,
          TError,
          Awaited<ReturnType<typeof revokeNexusProjectPermissions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRevokeNexusProjectPermissions<
  TData = Awaited<ReturnType<typeof revokeNexusProjectPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RevokeNexusProjectPermissionsPathParameters,
  tLNexusRevokePermissionsRequest: BodyType<TLNexusRevokePermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeNexusProjectPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Revoke Nexus Project Permissions
 */

export function useRevokeNexusProjectPermissions<
  TData = Awaited<ReturnType<typeof revokeNexusProjectPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: RevokeNexusProjectPermissionsPathParameters,
  tLNexusRevokePermissionsRequest: BodyType<TLNexusRevokePermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeNexusProjectPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRevokeNexusProjectPermissionsQueryOptions(
    { projectId },
    tLNexusRevokePermissionsRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Revoke Nexus Project Permissions
 */
export const prefetchRevokeNexusProjectPermissionsQuery = async <
  TData = Awaited<ReturnType<typeof revokeNexusProjectPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: RevokeNexusProjectPermissionsPathParameters,
  tLNexusRevokePermissionsRequest: BodyType<TLNexusRevokePermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeNexusProjectPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRevokeNexusProjectPermissionsQueryOptions(
    { projectId },
    tLNexusRevokePermissionsRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Revoke Nexus Project Permissions
 */
export const invalidateRevokeNexusProjectPermissions = async (
  queryClient: QueryClient,
  { projectId }: RevokeNexusProjectPermissionsPathParameters,
  tLNexusRevokePermissionsRequest: BodyType<TLNexusRevokePermissionsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRevokeNexusProjectPermissionsQueryKey({ projectId }, tLNexusRevokePermissionsRequest) },
    options,
  );

  return queryClient;
};
